import { currentLocale } from '@/lib/locale';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { CheckCircle, XCircle, Package, AlertCircle, Laptop, Ban, ShoppingCart, RefreshCw, Trash2 } from 'lucide-react';
import { DynamicSelect } from '@/components/inventory/DynamicSelect';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useQuipteamsKits, useQuipteamsOffices } from '@/hooks/useApprovals';
import type { ApprovalForPurchase, QuipteamsKit } from '../../../../shared/types';

type ActionType = 'approve' | 'reject' | 'markPurchased' | 'confirmNeeded' | 'confirmNotNeeded' | 'orderFromQuipteams' | 'cancel';

interface ApprovalActionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  approval: ApprovalForPurchase | null;
  actionType: ActionType;
  onConfirm: (data: any) => void;
  isLoading?: boolean;
}

export function ApprovalActionDialog({
  open,
  onOpenChange,
  approval,
  actionType,
  onConfirm,
  isLoading = false,
}: ApprovalActionDialogProps) {
  const { t } = useTranslation();
  const [notes, setNotes] = useState('');
  const [deviceType, setDeviceType] = useState('');
  const [deviceSpecs, setDeviceSpecs] = useState('');
  const [justification, setJustification] = useState('');
  const [techAssignedName, setTechAssignedName] = useState('');
  const [financeAssignedName, setFinanceAssignedName] = useState('');
  const [selectedKit, setSelectedKit] = useState<QuipteamsKit | null>(null);
  const [recipientPhone, setRecipientPhone] = useState('');
  const [recipientAddress, setRecipientAddress] = useState('');
  const [recipientCountry, setRecipientCountry] = useState('');
  const [cancelReason, setCancelReason] = useState('');
  const [deliveryMode, setDeliveryMode] = useState<'office' | 'manual'>('office');
  const [selectedOfficeId, setSelectedOfficeId] = useState('');

  const isOrdering = open && actionType === 'orderFromQuipteams';
  // Kits también en "Confirmar Necesidad de Hardware" (desplegable, para no tipear a mano)
  const kitsQuery = useQuipteamsKits(isOrdering || (open && actionType === 'confirmNeeded'));
  const officesQuery = useQuipteamsOffices(isOrdering);

  // Offices with the hire's country first
  const offices = (officesQuery.data?.offices ?? []).slice().sort((a, b) => {
    const c = (approval?.country || '').toLowerCase();
    const am = a.country.toLowerCase().includes(c) || c.includes(a.country.toLowerCase()) ? 0 : 1;
    const bm = b.country.toLowerCase().includes(c) || c.includes(b.country.toLowerCase()) ? 0 : 1;
    return am - bm || a.name.localeCompare(b.name);
  });
  const selectedOffice = offices.find((o) => o.id === selectedOfficeId) || null;

  const handleSelectOffice = (officeId: string) => {
    setSelectedOfficeId(officeId);
    const office = offices.find((o) => o.id === officeId);
    if (office) {
      setRecipientAddress(office.address);
      setRecipientCountry(office.country);
      setRecipientPhone(
        office.phone ? `${office.phoneCountryCode ? office.phoneCountryCode + ' ' : ''}${office.phone}` : ''
      );
    }
  };

  useEffect(() => {
    if (!open) {
      setSelectedKit(null);
      setRecipientPhone('');
      setRecipientAddress('');
      setRecipientCountry('');
      setDeliveryMode('office');
      setSelectedOfficeId('');
    } else if (actionType === 'orderFromQuipteams' && approval) {
      setRecipientCountry(approval.country || '');
    }
  }, [open, actionType, approval]);

  const handleConfirm = () => {
    if (actionType === 'confirmNeeded') {
      if (!deviceType.trim()) {
        alert(t('approvals.dialog.device_required'));
        return;
      }
      onConfirm({
        deviceType,
        deviceSpecs,
        justification,
        techAssignedName: techAssignedName || undefined,
      });
    } else if (actionType === 'confirmNotNeeded') {
      onConfirm({});
    } else if (actionType === 'approve') {
      onConfirm({ notes, financeAssignedName: financeAssignedName || undefined });
    } else if (actionType === 'reject') {
      if (!notes.trim()) {
        alert(t('approvals.dialog.notes_required'));
        return;
      }
      onConfirm({ notes });
    } else if (actionType === 'markPurchased') {
      onConfirm({});
    } else if (actionType === 'orderFromQuipteams') {
      if (!selectedKit) return;
      onConfirm({
        kitId: selectedKit.id,
        kitName: selectedKit.name,
        recipientPhone: recipientPhone.trim() || undefined,
        recipientAddress: recipientAddress.trim() || undefined,
        recipientCountry: recipientCountry.trim() || undefined,
      });
    } else if (actionType === 'cancel') {
      onConfirm({ reason: cancelReason.trim() || undefined });
    }

    // Reset form
    setNotes('');
    setDeviceType('');
    setDeviceSpecs('');
    setJustification('');
    setTechAssignedName('');
    setFinanceAssignedName('');
    setSelectedKit(null);
  };

  const handleCancel = () => {
    setNotes('');
    setDeviceType('');
    setDeviceSpecs('');
    setJustification('');
    setTechAssignedName('');
    setFinanceAssignedName('');
    setSelectedKit(null);
    setRecipientPhone('');
    setRecipientAddress('');
    setCancelReason('');
    onOpenChange(false);
  };

  if (!approval) return null;

  const getDialogConfig = () => {
    switch (actionType) {
      case 'confirmNeeded':
        return {
          title: t('approvals.dialog.confirm_needed_title'),
          icon: <Laptop className="h-6 w-6 text-blue-600" />,
          confirmText: t('approvals.dialog.confirm_needed_text'),
          confirmClass: 'bg-blue-600 hover:bg-blue-700',
          description: t('approvals.dialog.confirm_needed_desc', { name: approval.employeeName }),
        };
      case 'confirmNotNeeded':
        return {
          title: t('approvals.dialog.confirm_not_needed_title'),
          icon: <Ban className="h-6 w-6 text-gray-600" />,
          confirmText: t('approvals.dialog.confirm_not_needed_text'),
          confirmClass: 'bg-gray-600 hover:bg-gray-700',
          description: t('approvals.dialog.confirm_not_needed_desc', { name: approval.employeeName }),
        };
      case 'approve':
        return {
          title: t('approvals.dialog.approve_title'),
          icon: <CheckCircle className="h-6 w-6 text-green-600" />,
          confirmText: t('approvals.dialog.approve_text'),
          confirmClass: 'bg-green-600 hover:bg-green-700',
          description: t('approvals.dialog.approve_desc', { device: approval.deviceType, name: approval.employeeName }),
        };
      case 'reject':
        return {
          title: t('approvals.dialog.reject_title'),
          icon: <XCircle className="h-6 w-6 text-red-600" />,
          confirmText: t('approvals.dialog.reject_text'),
          confirmClass: 'bg-red-600 hover:bg-red-700',
          description: t('approvals.dialog.reject_desc', { device: approval.deviceType, name: approval.employeeName }),
        };
      case 'markPurchased':
        return {
          title: t('approvals.dialog.mark_purchased_title'),
          icon: <Package className="h-6 w-6 text-blue-600" />,
          confirmText: t('approvals.dialog.mark_purchased_text'),
          confirmClass: 'bg-blue-600 hover:bg-blue-700',
          description: t('approvals.dialog.mark_purchased_desc', { name: approval.employeeName }),
        };
      case 'orderFromQuipteams':
        return {
          title: t('approvals.dialog.pedir_a_quipteams'),
          icon: <ShoppingCart className="h-6 w-6 text-indigo-600" />,
          confirmText: 'Confirmar pedido',
          confirmClass: 'bg-indigo-600 hover:bg-indigo-700',
          description: `Seleccioná el kit para ${approval.employeeName} (${approval.area})`,
        };
      case 'cancel':
        return {
          title: t('approvals.dialog.cancelar_solicitud'),
          icon: <Trash2 className="h-6 w-6 text-red-500" />,
          confirmText: 'Sí, cancelar',
          confirmClass: 'bg-red-600 hover:bg-red-700',
          description: `Cancelar la solicitud de equipo para ${approval.employeeName}. Usá esto cuando recuperaste una compu y ya no hace falta comprar.`,
        };
      default:
        return {
          title: '',
          icon: null,
          confirmText: '',
          confirmClass: '',
          description: '',
        };
    }
  };

  const config = getDialogConfig();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={actionType === 'orderFromQuipteams' ? 'max-w-lg' : 'max-w-md'}>
        <DialogHeader>
          <div className="flex items-center gap-3">
            {config.icon}
            <DialogTitle>{config.title}</DialogTitle>
          </div>
        </DialogHeader>

        <div className="space-y-4">
          {/* Approval Details */}
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription className="ml-2">
              {config.description}
            </AlertDescription>
          </Alert>

          {/* Show approval details for existing approvals */}
          {actionType !== 'confirmNeeded' && actionType !== 'confirmNotNeeded' && (
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">{t('approvals.dialog.device_label')}</span>
                <span className="font-medium">{approval.deviceType}</span>
              </div>
              {approval.estimatedCost && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{t('approvals.dialog.cost_label')}</span>
                  <span className="font-medium">
                    {approval.currency} {approval.estimatedCost?.toLocaleString(currentLocale())}
                  </span>
                </div>
              )}
              {approval.justification && (
                <div className="flex flex-col gap-1">
                  <span className="text-muted-foreground">{t('approvals.dialog.justification_label')}</span>
                  <span className="text-sm italic">{approval.justification}</span>
                </div>
              )}
            </div>
          )}

          {/* Action-specific fields */}
          {actionType === 'confirmNeeded' && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="deviceType">{t('approvals.dialog.device_type_label')}</Label>
                <Select
                  value={deviceType}
                  onValueChange={(name) => {
                    setDeviceType(name);
                    const kit = kitsQuery.data?.find((k) => k.name === name);
                    if (kit) {
                      setDeviceSpecs(
                        kit.items.map((it) => (it.specs ? `${it.name} — ${it.specs}` : it.name)).join('; ')
                      );
                    }
                  }}
                >
                  <SelectTrigger id="deviceType">
                    <SelectValue
                      placeholder={kitsQuery.isLoading ? t('approvals.dialog.cargando_kits') : t('approvals.dialog.elegi_un_kit')}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {kitsQuery.data?.map((kit) => (
                      <SelectItem key={kit.id} value={kit.name}>
                        {kit.name}{kit.tag ? ` · ${kit.tag}` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {kitsQuery.data && kitsQuery.data.length === 0 && (
                  <p className="text-xs text-muted-foreground">{t('approvals.dialog.no_hay_kits_en_quipteams_cargalos')}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="deviceSpecs">{t('approvals.dialog.specs_label')}</Label>
                <Textarea
                  id="deviceSpecs"
                  placeholder={t('approvals.dialog.specs_placeholder')}
                  value={deviceSpecs}
                  onChange={(e) => setDeviceSpecs(e.target.value)}
                  rows={2}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="justification">{t('approvals.dialog.justification_field_label')}</Label>
                <Textarea
                  id="justification"
                  placeholder={t('approvals.dialog.justification_field_placeholder')}
                  value={justification}
                  onChange={(e) => setJustification(e.target.value)}
                  rows={2}
                />
              </div>
              <div className="space-y-2">
                <Label>{t('approvals.dialog.tech_assigned_label')}</Label>
                <DynamicSelect
                  optionType="techMember"
                  value={techAssignedName}
                  onValueChange={setTechAssignedName}
                  placeholder={t('approvals.dialog.tech_assigned_placeholder')}
                />
              </div>
            </div>
          )}

          {actionType === 'confirmNotNeeded' && (
            <div className="text-sm text-muted-foreground">
              <p>{t('approvals.dialog.cancel_action_note')}</p>
              <p className="mt-2">{t('approvals.dialog.no_hardware_note')}</p>
            </div>
          )}

          {actionType === 'approve' && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>{t('approvals.dialog.finance_assigned_label')}</Label>
                <DynamicSelect
                  optionType="financeMember"
                  value={financeAssignedName}
                  onValueChange={setFinanceAssignedName}
                  placeholder={t('approvals.dialog.finance_assigned_placeholder')}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="approveNotes">{t('approvals.dialog.notes_optional_label')}</Label>
                <Textarea
                  id="approveNotes"
                  placeholder={t('approvals.dialog.notes_optional_placeholder')}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                />
              </div>
            </div>
          )}

          {actionType === 'reject' && (
            <div className="space-y-2">
              <Label htmlFor="rejectNotes">{t('approvals.dialog.reject_notes_label')}</Label>
              <Textarea
                id="rejectNotes"
                placeholder={t('approvals.dialog.reject_notes_placeholder')}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                required
              />
              <p className="text-xs text-muted-foreground">
                {t('approvals.dialog.reject_notes_hint')}
              </p>
            </div>
          )}

          {actionType === 'cancel' && (
            <div className="space-y-2">
              <Label htmlFor="cancelReason">Motivo <span className="text-muted-foreground text-xs">(opcional)</span></Label>
              <Textarea
                id="cancelReason"
                placeholder={t('approvals.dialog.ej_se_recupero_compu_de_baja')}
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                rows={2}
              />
            </div>
          )}

          {actionType === 'orderFromQuipteams' && (
            <div className="space-y-4">
              {/* Recipient contact info */}
              <div className="rounded-md border p-3 space-y-3 bg-muted/30">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                  {t('approvals.dialog.datos_del_destinatario_para_quipteams')}
                </p>

                {/* Read-only fields */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                  <div>
                    <span className="text-muted-foreground">Nombre:</span>
                    <span className="ml-1 font-medium">{approval.employeeName}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Email:</span>
                    <span className="ml-1 font-medium">{approval.employeeEmail}</span>
                  </div>
                  {approval.startDate && (
                    <div>
                      <span className="text-muted-foreground">{t('approvals.dialog.fecha_ingreso')}</span>
                      <span className="ml-1 font-medium">{approval.startDate}</span>
                    </div>
                  )}
                </div>

                {/* Delivery destination: office suggestion or manual */}
                <div className="space-y-3">
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      size="sm"
                      variant={deliveryMode === 'office' ? 'default' : 'outline'}
                      className="h-8 text-xs"
                      onClick={() => setDeliveryMode('office')}
                    >
                      {t('approvals.dialog.enviar_a_una_oficina')}
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant={deliveryMode === 'manual' ? 'default' : 'outline'}
                      className="h-8 text-xs"
                      onClick={() => setDeliveryMode('manual')}
                    >
                      {t('approvals.dialog.otra_direccion')}
                    </Button>
                  </div>

                  {deliveryMode === 'office' ? (
                    <div className="space-y-1.5">
                      <Label className="text-xs">
                        {t('approvals.dialog.oficina_de_entrega')} <span className="text-red-500">*</span>
                      </Label>
                      <Select value={selectedOfficeId} onValueChange={handleSelectOffice}>
                        <SelectTrigger className="h-8 text-sm">
                          <SelectValue placeholder={officesQuery.isLoading ? t('approvals.dialog.cargando_oficinas') : t('approvals.dialog.elegi_una_oficina')} />
                        </SelectTrigger>
                        <SelectContent>
                          {offices.map((o) => (
                            <SelectItem key={o.id} value={o.id}>
                              {o.name} — {o.country}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {selectedOffice && (
                        <div className="rounded bg-background border p-2 text-xs text-muted-foreground space-y-0.5">
                          <div>📍 {selectedOffice.address}</div>
                          {selectedOffice.contactName && <div>👤 {selectedOffice.contactName}</div>}
                          {selectedOffice.phone && (
                            <div>📞 {selectedOffice.phoneCountryCode ? selectedOffice.phoneCountryCode + ' ' : ''}{selectedOffice.phone}</div>
                          )}
                        </div>
                      )}
                      {!officesQuery.isLoading && offices.length === 0 && (
                        <p className="text-xs text-muted-foreground">{t('approvals.dialog.no_hay_oficinas_configuradas_usa_otra')}</p>
                      )}
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div className="space-y-1.5">
                        <Label htmlFor="recipientCountry" className="text-xs">
                          {t('approvals.dialog.pais')} <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="recipientCountry"
                          placeholder="Argentina"
                          value={recipientCountry}
                          onChange={(e) => setRecipientCountry(e.target.value)}
                          className="h-8 text-sm"
                        />
                      </div>
                      <div className="space-y-1.5">
                        <Label htmlFor="recipientPhone" className="text-xs">{t('approvals.dialog.telefono')}</Label>
                        <Input
                          id="recipientPhone"
                          placeholder="+54 11 1234-5678"
                          value={recipientPhone}
                          onChange={(e) => setRecipientPhone(e.target.value)}
                          className="h-8 text-sm"
                        />
                      </div>
                      <div className="col-span-2 space-y-1.5">
                        <Label htmlFor="recipientAddress" className="text-xs">{t('approvals.dialog.direccion_de_entrega')}</Label>
                        <Input
                          id="recipientAddress"
                          placeholder={t('approvals.dialog.av_corrientes_1234_caba')}
                          value={recipientAddress}
                          onChange={(e) => setRecipientAddress(e.target.value)}
                          className="h-8 text-sm"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Kit selector */}
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{t('approvals.dialog.selecciona_el_kit')}</p>
              {kitsQuery.isLoading && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground py-4 justify-center">
                  <RefreshCw className="h-4 w-4 animate-spin" />
                  {t('approvals.dialog.cargando_kits_disponibles')}
                </div>
              )}
              {kitsQuery.error && (
                <p className="text-sm text-red-500">{t('approvals.dialog.error_al_cargar_kits')} {kitsQuery.error.message}</p>
              )}
              {kitsQuery.data && kitsQuery.data.length === 0 && (
                <p className="text-sm text-muted-foreground">{t('approvals.dialog.no_hay_kits_disponibles_en_quipteams')}</p>
              )}
              {kitsQuery.data?.map((kit) => (
                <button
                  key={kit.id}
                  type="button"
                  onClick={() => setSelectedKit(kit)}
                  className={`w-full text-left rounded-lg border p-3 transition-colors ${
                    selectedKit?.id === kit.id
                      ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-950/30'
                      : 'border-border hover:border-indigo-300 hover:bg-muted/40'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-sm">{kit.name}</span>
                    {kit.tag && (
                      <span className="text-xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
                        {kit.tag}
                      </span>
                    )}
                  </div>
                  {kit.items.length > 0 && (
                    <ul className="mt-1.5 space-y-0.5">
                      {kit.items.map((item, i) => (
                        <li key={i} className="text-xs text-muted-foreground">
                          • {item.name}{item.specs ? ` — ${item.specs}` : ''}
                        </li>
                      ))}
                    </ul>
                  )}
                </button>
              ))}
            </div>
          )}

        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleCancel} disabled={isLoading}>
            {t('approvals.dialog.cancel')}
          </Button>
          <Button
            className={config.confirmClass}
            onClick={handleConfirm}
            disabled={isLoading || (actionType === 'orderFromQuipteams' && (!selectedKit || !recipientCountry.trim()))}
          >
            {isLoading ? t('approvals.dialog.processing') : config.confirmText}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
